import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faUserCheck, 
  faSpinner,
  faCheck,
  faTimes,
  faCheckCircle,
  faTimesCircle,
  faUser,
  faEnvelope,
  faPhone,
  faIdBadge,
  faCalendar,
  faClock,
  faExclamationTriangle,
  faInfoCircle,
  faUsers,
  faUserShield,
  faTag,
  faEdit,
  faSearch,
  faFilter,
  faDownload,
  faChartBar,
  faSync,
  faBell
} from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import { toast, ToastContainer } from 'react-toastify';
import VerificationDashboard from './VerificationDashboard';
import config from '../config/config';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';
import './UserVerificationPage.css';

const UserVerificationPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  
  // State management
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('PENDING'); // PENDING, APPROVED, REJECTED, ALL, DASHBOARD
  const [submitting, setSubmitting] = useState(false);
  
  // Advanced search and pagination state
  const [filters, setFilters] = useState({
    search: '',
    statuses: ['PENDING'], // Start with PENDING status
    teams: [],
    requestType: 'ALL',
    requestedFrom: null,
    requestedTo: null,
    page: 0,
    size: 20,
    sortBy: 'requestedAt',
    sortDirection: 'DESC'
  });
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [searchDebounce, setSearchDebounce] = useState(null);
  
  // Bulk operations state
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkComments, setBulkComments] = useState('');
  const [bulkTeamName, setBulkTeamName] = useState('');
  const [bulkSendInEnglish, setBulkSendInEnglish] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Modal state
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [actionComments, setActionComments] = useState('');
  
  // New form fields for approve/reject
  const [teamName, setTeamName] = useState('');
  const [sendInEnglish, setSendInEnglish] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Search term state (separate for UI input)
  const [searchTerm, setSearchTerm] = useState('');
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);

  // Edit user state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    email: '',
    phone: '',
    iboName: '',
    uplinePlatinumIbo: '',
    teamName: '',
    emailVerified: false,
    phoneVerified: false,
    userVerified: false,
    isFirstContractYear: false
  });
  const [updatingUser, setUpdatingUser] = useState(false);
  const [editModalError, setEditModalError] = useState(null);

  // Detail view modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDetailRequest, setSelectedDetailRequest] = useState(null);

  // Resend email state
  const [resendingEmail, setResendingEmail] = useState(null); // Stores request ID currently being processed

  // Refresh KPI state
  const [refreshingKpi, setRefreshingKpi] = useState(null); // Stores IBO number currently being refreshed

  // Fetch verification requests using advanced search
  const fetchVerificationRequests = useCallback(async () => {
    if (activeTab === 'DASHBOARD') return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/verifications/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(filters)
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. You need USER_VERIFICATION feature access.');
        }
        throw new Error(`Failed to fetch verification requests. Status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setVerificationRequests(result.data.content || []);
        setTotalPages(result.data.totalPages || 0);
        setTotalElements(result.data.totalElements || 0);
      } else {
        throw new Error(result.message || 'Failed to load verification requests');
      }
    } catch (err) {
      console.error('Error fetching verification requests:', err);
      setError(err.message);
      toast.error('Failed to load verification requests: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, activeTab, getAccessToken]);

  // Fetch requests when filters change
  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        return;
      }

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/verifications/analytics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data) {
          setAnalytics(result.data);
        }
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      // Don't show error toast for analytics - it's non-critical
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchVerificationRequests();
    fetchAnalytics(); // Fetch analytics on component mount
  }, [fetchVerificationRequests, fetchAnalytics]);

  const handleBack = () => {
    navigate('/');
  };

  // Handle tab change and update filters accordingly
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Update filters based on selected tab
    if (tab === 'DASHBOARD') {
      // Don't change filters for dashboard
      return;
    } else if (tab === 'ALL') {
      // Show all statuses
      setFilters(prev => ({ ...prev, statuses: [], page: 0 }));
    } else {
      // Show specific status (PENDING, APPROVED, REJECTED)
      setFilters(prev => ({ ...prev, statuses: [tab], page: 0 }));
    }
  };

  // Group requests by month
  const groupRequestsByMonth = (requests) => {
    const grouped = {};
    
    requests.forEach(request => {
      // Convert Unix timestamp (seconds) to milliseconds for JavaScript Date
      const date = new Date(request.requestedAt * 1000);
      const monthYear = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(request);
    });
    
    // Sort months in descending order (most recent first)
    return Object.keys(grouped)
      .sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateB.getTime() - dateA.getTime();
      })
      .map(monthYear => ({
        monthYear,
        requests: grouped[monthYear]
      }));
  };

  // Fetch teams from the API
  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch teams. Status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        setTeams(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to load teams');
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      // Don't show error to user, just log it
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleApproveRequest = async (request) => {
    setSelectedRequest(request);
    setActionType('approve');
    setActionComments('');
    // Pre-select the user's existing team if available
    setTeamName(request.userTeamName || '');
    setSendInEnglish(false);
    setShowActionModal(true);
    
    // Fetch teams for the dropdown
    await fetchTeams();
  };

  const handleRejectRequest = (request) => {
    setSelectedRequest(request);
    setActionType('reject');
    setActionComments('');
    setTeamName('');
    setSendInEnglish(false);
    setShowActionModal(true);
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedRequest || !actionType) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const endpoint = `${config.API_BASE_URL}/um/admin/v1/verifications/${selectedRequest.id}/${actionType}`;
      
      // Build request body based on action type
      const requestBody = {
        approverIboNumber: 'ADMIN',
        comments: actionComments || undefined,
        sendInEnglish: sendInEnglish || undefined
      };

      // Add teamName only for approve action
      if (actionType === 'approve' && teamName) {
        requestBody.teamName = teamName;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${actionType} verification request. Status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        setShowActionModal(false);
        setSelectedRequest(null);
        setActionComments('');
        setTeamName('');
        setSendInEnglish(false);
        
        // Refresh the list
        await fetchVerificationRequests();
        
        toast.success(`Verification request ${actionType === 'approve' ? 'approved' : 'rejected'} successfully!`);
      } else {
        throw new Error(result.message || `Failed to ${actionType} verification request`);
      }
    } catch (err) {
      console.error(`Error ${actionType}ing verification request:`, err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Search handling with debounce
  const handleSearchChange = (value) => {
    // Clear existing timeout
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }
    
    // Update search term for UI
    setSearchTerm(value);
    
    // Set new timeout for API call
    const timeout = setTimeout(() => {
      // Update filters after debounce delay
      setFilters(prev => ({ ...prev, search: value, page: 0 }));
    }, 300);
    
    setSearchDebounce(timeout);
  };

  // Filter change handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 0 }));
  };

  const handleClearFilters = () => {
    setSearchTerm(''); // Clear search term UI
    setFilters({
      search: '',
      statuses: [],
      teams: [],
      requestType: 'ALL',
      requestedFrom: null,
      requestedTo: null,
      page: 0,
      size: 20,
      sortBy: 'requestedAt',
      sortDirection: 'DESC'
    });
    setShowFilters(false);
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newSize) => {
    setFilters(prev => ({ ...prev, size: parseInt(newSize), page: 0 }));
  };

  // Bulk selection handlers
  const handleSelectRequest = (requestId) => {
    const newSelected = new Set(selectedRequests);
    if (newSelected.has(requestId)) {
      newSelected.delete(requestId);
    } else {
      newSelected.add(requestId);
    }
    setSelectedRequests(newSelected);
    setSelectAll(newSelected.size === verificationRequests.length);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = new Set(verificationRequests.map(r => r.id));
      setSelectedRequests(allIds);
    } else {
      setSelectedRequests(new Set());
    }
    setSelectAll(checked);
  };

  // Bulk operations handler
  const handleBulkOperation = async () => {
    if (selectedRequests.size === 0) {
      toast.warning('Please select at least one request');
      return;
    }

    try {
      setBulkProcessing(true);
      const token = await getAccessToken();
      
      const requestBody = {
        requestIds: Array.from(selectedRequests),
        action: bulkAction,
        comments: bulkComments,
        teamName: bulkAction === 'APPROVE' ? bulkTeamName : null,
        sendInEnglish: bulkSendInEnglish
      };
      
      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/verifications/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      const result = await response.json();
      if (result.status === 'success') {
        toast.success(`Bulk operation completed: ${result.data.successCount} succeeded, ${result.data.failureCount} failed`);
        setSelectedRequests(new Set());
        setSelectAll(false);
        setShowBulkModal(false);
        setBulkComments('');
        setBulkTeamName('');
        fetchVerificationRequests();
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      toast.error('Bulk operation failed: ' + err.message);
    } finally {
      setBulkProcessing(false);
    }
  };

  // Export handler
  const handleExport = async (format) => {
    try {
      toast.info(`Preparing ${format} export...`);
      const token = await getAccessToken();
      
      const response = await fetch(
        `${config.API_BASE_URL}/um/admin/v1/verifications/export?format=${format}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(filters)
        }
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verification_requests_${Date.now()}.${format.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`${format} export downloaded successfully!`);
    } catch (err) {
      toast.error('Export failed: ' + err.message);
    }
  };

  // Detail modal handlers
  const handleRowClick = (request, event) => {
    // Don't open modal if clicking on action buttons or checkbox
    if (event.target.closest('.action-btn') || event.target.closest('input[type="checkbox"]')) {
      return;
    }
    setSelectedDetailRequest(request);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedDetailRequest(null);
  };

  const handleEditUser = async (request) => {
    try {
      setSelectedUserForEdit(request);
      setEditModalError(null);
      setShowEditModal(true);
      setUpdatingUser(true); // Use this to show loading state while fetching
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      // Fetch complete user data using the userId
      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/get?userId=${request.userId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch user data');
      }

      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        const userData = result.data;
        
        // Populate form with complete user data from API
        setEditUserForm({
          email: userData.email || '',
          phone: userData.phone || '',
          iboName: userData.iboName || '',
          uplinePlatinumIbo: userData.uplinePlatinumIbo || '',
          teamName: userData.teamName || '',
          emailVerified: userData.emailVerified || false,
          phoneVerified: userData.phoneVerified || false,
          userVerified: userData.userVerified || false,
          isFirstContractYear: userData.isFirstContractYear || false
        });
      } else {
        throw new Error(result.message || 'Failed to load user data');
      }
      
      // Fetch teams for the dropdown if not already loaded
      if (teams.length === 0) {
        await fetchTeams();
      }
      
    } catch (err) {
      console.error('Error fetching user data:', err);
      setEditModalError(err.message);
      // Keep the modal open to show the error
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleEditUserFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditUserForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    // Don't submit if we're currently loading user data
    if (updatingUser) return;
    
    try {
      setUpdatingUser(true);
      setEditModalError(null);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      // Prepare update data
      const updateData = {
        userId: selectedUserForEdit.userId,
        ...editUserForm
      };

      const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update user');
      }

      if (data.status === 'success') {
        alert('User updated successfully!');
        
        // Close edit modal
        setShowEditModal(false);
        setSelectedUserForEdit(null);
        setEditModalError(null);
        setEditUserForm({
          email: '',
          phone: '',
          iboName: '',
          uplinePlatinumIbo: '',
          teamName: '',
          emailVerified: false,
          phoneVerified: false,
          userVerified: false,
          isFirstContractYear: false
        });

        // Refresh the verification requests list
        await fetchVerificationRequests();
      } else {
        throw new Error(data.message || 'Failed to update user');
      }

    } catch (err) {
      console.error('Error updating user:', err);
      setEditModalError(err.message);
    } finally {
      setUpdatingUser(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Check if timestamp is a Unix timestamp (number) or ISO string
    let date;
    if (typeof timestamp === 'number') {
      // Unix timestamp in seconds, convert to milliseconds
      date = new Date(timestamp * 1000);
    } else {
      // ISO date string
      date = new Date(timestamp);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return '#4caf50';
      case 'REJECTED':
        return '#f44336';
      case 'PENDING':
        return '#ff9800';
      default:
        return '#666';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED':
        return faCheckCircle;
      case 'REJECTED':
        return faTimesCircle;
      case 'PENDING':
        return faClock;
      default:
        return faInfoCircle;
    }
  };

  // Handler for resending approver notification email
  const handleResendEmail = async (request, event) => {
    // Prevent row click event from triggering
    if (event) {
      event.stopPropagation();
    }

    try {
      setResendingEmail(request.id);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(
        `${config.API_BASE_URL}/um/admin/v1/verifications/${request.id}/resend-notification`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to resend notification');
      }

      if (result.status === 'success') {
        toast.success('Notification email sent successfully to the approver!');
      } else {
        throw new Error(result.message || 'Failed to resend notification');
      }
    } catch (err) {
      console.error('Error resending notification:', err);
      toast.error('Failed to resend notification: ' + err.message);
    } finally {
      setResendingEmail(null);
    }
  };

  // Handler for refreshing KPI data
  const handleRefreshKpi = async (iboNumber, event) => {
    // Prevent row click event from triggering
    if (event) {
      event.stopPropagation();
    }

    try {
      setRefreshingKpi(iboNumber);

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(
        `${config.API_BASE_URL}/um/v1/admin/user/refresh-kpi?iboNumber=${encodeURIComponent(iboNumber)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to refresh KPI data');
      }

      if (result.status === 'success') {
        toast.success('KPI data refreshed successfully!');
        
        // Refresh the verification requests list to show updated data
        await fetchVerificationRequests();
      } else {
        throw new Error(result.message || 'Failed to refresh KPI data');
      }
    } catch (err) {
      console.error('Error refreshing KPI data:', err);
      toast.error('Failed to refresh KPI: ' + err.message);
    } finally {
      setRefreshingKpi(null);
    }
  };

  return (
    <div className="user-verification-container">
      <div className="user-verification-header">
        <button onClick={handleBack} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>
        
        <div className="header-content">
          <h2><FontAwesomeIcon icon={faUserCheck} /> User Verification</h2>
          <p>Review and manage user verification requests</p>
        </div>
        
        <div className="header-spacer"></div>
      </div>

      {/* Tabs */}
      <div className="verification-tabs">
        <button 
          className={`tab-button ${activeTab === 'PENDING' ? 'active' : ''}`}
          onClick={() => handleTabChange('PENDING')}
        >
          <FontAwesomeIcon icon={faClock} /> Pending
          {analytics && analytics.totalPending > 0 && (
            <span className="tab-badge">{analytics.totalPending}</span>
          )}
        </button>
        <button 
          className={`tab-button ${activeTab === 'APPROVED' ? 'active' : ''}`}
          onClick={() => handleTabChange('APPROVED')}
        >
          <FontAwesomeIcon icon={faCheckCircle} /> Approved
        </button>
        <button 
          className={`tab-button ${activeTab === 'REJECTED' ? 'active' : ''}`}
          onClick={() => handleTabChange('REJECTED')}
        >
          <FontAwesomeIcon icon={faTimesCircle} /> Rejected
        </button>
        <button 
          className={`tab-button ${activeTab === 'ALL' ? 'active' : ''}`}
          onClick={() => handleTabChange('ALL')}
        >
          <FontAwesomeIcon icon={faInfoCircle} /> All
        </button>
      </div>

      {/* Stats Banner */}
      {analytics && analytics.pendingOverThreshold > 0 && (
        <div className="stats-banner urgent">
          <div className="stats-banner-icon">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <div className="stats-banner-content">
            <strong>Urgent Attention Required!</strong>
            <span>{analytics.pendingOverThreshold} verification request{analytics.pendingOverThreshold > 1 ? 's' : ''} pending for more than 24 hours</span>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-bar-container">
        <div className="search-bar">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search by IBO Number, Email, Upline IBO, or Phone..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search-button"
              onClick={() => handleSearchChange('')}
              title="Clear search"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div className="loading-indicator">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Loading verification requests...</span>
        </div>
      ) : (
        <div className="verification-requests-container">
          {verificationRequests.length > 0 ? (
            <div className="verification-list-grouped">
              {groupRequestsByMonth(verificationRequests).map(({ monthYear, requests }) => (
                <div key={monthYear} className="month-group">
                  <div className="month-header">
                    <FontAwesomeIcon icon={faCalendar} />
                    <h3>{monthYear}</h3>
                    <span className="month-count">({requests.length} request{requests.length !== 1 ? 's' : ''})</span>
                  </div>
                  
                  <div className="verification-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Name</th>
                          <th>IBO #</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Secondary Phone</th>
                          <th>Team</th>
                          <th>Type</th>
                          <th>Requested</th>
                          {requests.some(r => r.respondedAt) && <th>Responded</th>}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((request) => (
                          <tr 
                            key={request.id} 
                            className={`status-${request.status.toLowerCase()} clickable-row`}
                            onClick={(e) => handleRowClick(request, e)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div 
                                className="status-badge" 
                                style={{ backgroundColor: getStatusColor(request.status) }}
                              >
                                <FontAwesomeIcon icon={getStatusIcon(request.status)} />
                                {request.status}
                              </div>
                            </td>
                            <td className="user-name">
                              <FontAwesomeIcon icon={faUser} className="icon-small" />
                              {request.userName || 'N/A'}
                              {request.userIsFirstContractYear && (
                                <span className="badge-new" title="First Contract Year">1st Year</span>
                              )}
                            </td>
                            <td className="ibo-number">{request.userIboNumber || 'N/A'}</td>
                            <td className="email">
                              <FontAwesomeIcon icon={faEnvelope} className="icon-small" />
                              {request.userEmail || 'N/A'}
                            </td>
                            <td className="phone">
                              <FontAwesomeIcon icon={faPhone} className="icon-small" />
                              {request.userPhone || 'N/A'}
                            </td>
                            <td className="phone">
                              {request.userSecondaryPhone 
                                ? <><FontAwesomeIcon icon={faPhone} className="icon-small" />{request.userSecondaryPhone}{request.userSecondaryPhoneVerified ? ' ✓' : ' ✗'}</>
                                : 'N/A'}
                            </td>
                            <td className="team">
                              <FontAwesomeIcon icon={faUsers} className="icon-small" />
                              {request.userTeamName || 'Not Assigned'}
                            </td>
                            <td className="request-type">
                              <span className={`request-type-badge ${request.requestType?.toLowerCase()}`}>
                                {request.requestType || 'N/A'}
                              </span>
                            </td>
                            <td className="date-time">
                              <FontAwesomeIcon icon={faClock} className="icon-small" />
                              {formatDate(request.requestedAt)}
                            </td>
                            {requests.some(r => r.respondedAt) && (
                              <td className="date-time">
                                {request.respondedAt ? (
                                  <>
                                    <FontAwesomeIcon icon={faClock} className="icon-small" />
                                    {formatDate(request.respondedAt)}
                                    {request.processingTimeInMinutes && (
                                      <small className="processing-time">({request.processingTimeInMinutes}m)</small>
                                    )}
                                  </>
                                ) : (
                                  '-'
                                )}
                              </td>
                            )}
                            <td className="actions">
                              {request.status === 'PENDING' && (
                                <>
                                  <button 
                                    className="action-btn refresh-kpi-btn"
                                    onClick={(e) => handleRefreshKpi(request.userIboNumber, e)}
                                    disabled={refreshingKpi === request.userIboNumber}
                                    title="Refresh KPI data from Amway"
                                  >
                                    <FontAwesomeIcon 
                                      icon={faSync} 
                                      spin={refreshingKpi === request.userIboNumber}
                                    />
                                  </button>
                                  <button 
                                    className="action-btn edit-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditUser(request);
                                    }}
                                    title="Edit user"
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </button>
                                  <button 
                                    className="action-btn approve-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApproveRequest(request);
                                    }}
                                    title="Approve"
                                  >
                                    <FontAwesomeIcon icon={faCheck} />
                                  </button>
                                  <button 
                                    className="action-btn reject-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRejectRequest(request);
                                    }}
                                    title="Reject"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                  <button 
                                    className="action-btn resend-email-btn"
                                    onClick={(e) => handleResendEmail(request, e)}
                                    disabled={resendingEmail === request.id}
                                    title="Resend notification email to approver"
                                  >
                                    <FontAwesomeIcon 
                                      icon={faBell}
                                    />
                                  </button>
                                </>
                              )}
                              {request.comments && (
                                <button 
                                  className="action-btn info-btn"
                                  title={`Comments: ${request.comments}`}
                                >
                                  <FontAwesomeIcon icon={faInfoCircle} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-records">
              <FontAwesomeIcon icon={faInfoCircle} />
              <p>No verification requests found.</p>
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && verificationRequests.length > 0 && (
            <div className="pagination-container">
              <div className="pagination-info">
                <span>
                  Showing {filters.page * filters.size + 1} to{' '}
                  {Math.min((filters.page + 1) * filters.size, totalElements)} of{' '}
                  {totalElements} results
                </span>
              </div>

              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 0}
                  title="Previous page"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Previous
                </button>

                <div className="page-numbers">
                  {/* First page */}
                  {filters.page > 2 && (
                    <>
                      <button
                        className="page-number-button"
                        onClick={() => handlePageChange(0)}
                      >
                        1
                      </button>
                      {filters.page > 3 && <span className="pagination-ellipsis">...</span>}
                    </>
                  )}

                  {/* Pages around current page */}
                  {Array.from({ length: totalPages }, (_, i) => i)
                    .filter(page => 
                      page >= filters.page - 2 && 
                      page <= filters.page + 2
                    )
                    .map(page => (
                      <button
                        key={page}
                        className={`page-number-button ${page === filters.page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page + 1}
                      </button>
                    ))
                  }

                  {/* Last page */}
                  {filters.page < totalPages - 3 && (
                    <>
                      {filters.page < totalPages - 4 && <span className="pagination-ellipsis">...</span>}
                      <button
                        className="page-number-button"
                        onClick={() => handlePageChange(totalPages - 1)}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page >= totalPages - 1}
                  title="Next page"
                >
                  Next
                  <FontAwesomeIcon icon={faArrowLeft} style={{ transform: 'rotate(180deg)' }} />
                </button>
              </div>

              <div className="page-size-selector">
                <label htmlFor="pageSize">Items per page:</label>
                <select
                  id="pageSize"
                  value={filters.size}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  className="page-size-select"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUserForEdit && (
        <div className="modal-overlay">
          <div className="edit-user-modal">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faEdit} />
                Edit User Information
              </h3>
              <button
                className="close-button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditModalError(null);
                }}
                disabled={updatingUser}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              {/* Error Message within Modal */}
              {editModalError && (
                <div className="modal-error-message">
                  <FontAwesomeIcon icon={faTimes} />
                  {editModalError}
                </div>
              )}

              <div className="user-info-banner">
                <div className="info-item">
                  <strong>IBO Number:</strong> {selectedUserForEdit.userIboNumber}
                </div>
                <div className="info-item">
                  <strong>User ID:</strong> {selectedUserForEdit.userId}
                </div>
              </div>

              {updatingUser && !editModalError ? (
                <div className="loading-user-data">
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  <p>Loading user data...</p>
                </div>
              ) : (
                <form onSubmit={handleUpdateUser} className="edit-user-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-email">Email Address</label>
                      <input
                        type="email"
                        id="edit-email"
                        name="email"
                        value={editUserForm.email}
                        onChange={handleEditUserFormChange}
                        disabled={updatingUser}
                      />
                    </div>

                  <div className="form-group">
                    <label htmlFor="edit-phone">Phone Number</label>
                    <input
                      type="tel"
                      id="edit-phone"
                      name="phone"
                      value={editUserForm.phone}
                      onChange={handleEditUserFormChange}
                      disabled={updatingUser}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-iboName">IBO Name</label>
                    <input
                      type="text"
                      id="edit-iboName"
                      name="iboName"
                      value={editUserForm.iboName}
                      onChange={handleEditUserFormChange}
                      disabled={updatingUser}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-uplinePlatinumIbo">Upline Platinum IBO</label>
                    <input
                      type="text"
                      id="edit-uplinePlatinumIbo"
                      name="uplinePlatinumIbo"
                      value={editUserForm.uplinePlatinumIbo}
                      onChange={handleEditUserFormChange}
                      disabled={updatingUser}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-teamName">Team</label>
                  {loadingTeams ? (
                    <div className="loading-teams">
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Loading teams...</span>
                    </div>
                  ) : (
                    <select
                      id="edit-teamName"
                      name="teamName"
                      value={editUserForm.teamName}
                      onChange={handleEditUserFormChange}
                      disabled={updatingUser}
                      className="team-select"
                    >
                      <option value="">Select a team</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.name}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-section-title">Verification Status</div>
                
                <div className="checkbox-group-row">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="emailVerified"
                        checked={editUserForm.emailVerified}
                        onChange={handleEditUserFormChange}
                        disabled={updatingUser}
                      />
                      <span>Email Verified</span>
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="phoneVerified"
                        checked={editUserForm.phoneVerified}
                        onChange={handleEditUserFormChange}
                        disabled={updatingUser}
                      />
                      <span>Phone Verified</span>
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="userVerified"
                        checked={editUserForm.userVerified}
                        onChange={handleEditUserFormChange}
                        disabled={updatingUser}
                      />
                      <span>User Verified (Active)</span>
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isFirstContractYear"
                        checked={editUserForm.isFirstContractYear}
                        onChange={handleEditUserFormChange}
                        disabled={updatingUser}
                      />
                      <span>First Contract Year</span>
                    </label>
                  </div>
                </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditModalError(null);
                      }}
                      disabled={updatingUser}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="submit-button"
                      disabled={updatingUser}
                    >
                      {updatingUser ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin />
                          Updating...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheck} />
                          Update User
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Modal (Approve/Reject) */}
      {showActionModal && selectedRequest && (
        <div className="modal-overlay">
          <div className={`modal-content ${actionType === 'reject' ? 'reject-modal' : ''}`}>
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon 
                  icon={actionType === 'approve' ? faCheck : faTimes} 
                  className={actionType === 'approve' ? 'approve-icon' : 'reject-icon'}
                />
                {actionType === 'approve' ? 'Approve' : 'Reject'} Verification Request
              </h3>
              <button 
                className="close-button"
                onClick={() => setShowActionModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {actionType === 'reject' && (
              <div className="warning-banner">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <p>The user will need to resubmit their verification request after rejection.</p>
              </div>
            )}
            
            <div className="modal-body">
              <div className="request-summary">
                <h4>Request Details</h4>
                <p><strong>User:</strong> {selectedRequest.userName}</p>
                <p><strong>IBO Number:</strong> {selectedRequest.userIboNumber}</p>
                <p><strong>Email:</strong> {selectedRequest.userEmail}</p>
                <p>
                  <strong>Request Type:</strong>{' '}
                  <span className={`request-type-badge ${selectedRequest.requestType?.toLowerCase()}`}>
                    {selectedRequest.requestType || 'N/A'}
                  </span>
                </p>
                {selectedRequest.userTeamName && (
                  <p><strong>Current Team:</strong> {selectedRequest.userTeamName}</p>
                )}
                <p>
                  <strong>Approver:</strong>{' '}
                  {selectedRequest.isAdminRequest ? (
                    <span className="approver-badge admin">
                      <FontAwesomeIcon icon={faUserShield} /> Admin
                    </span>
                  ) : (
                    <span className="approver-badge upline">
                      <FontAwesomeIcon icon={faUser} /> {selectedRequest.approverName || 'Upline'}
                    </span>
                  )}
                </p>
                <p><strong>Requested:</strong> {formatDate(selectedRequest.requestedAt)}</p>
              </div>

              <form onSubmit={handleActionSubmit} className="action-form">
                {actionType === 'approve' && (
                  <div className="form-group">
                    <label htmlFor="teamName">
                      Team Name (Optional)
                    </label>
                    {loadingTeams ? (
                      <div className="loading-teams">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Loading teams...</span>
                      </div>
                    ) : (
                      <select
                        id="teamName"
                        name="teamName"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        className="team-select"
                      >
                        <option value="">Select a team (Optional)</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.name}>
                            {team.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <small className="form-hint">
                      {selectedRequest.userTeamName ? (
                        <>Current team: <strong>{selectedRequest.userTeamName}</strong> (You can change this)</>
                      ) : (
                        'Assign the user to a specific team'
                      )}
                    </small>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="comments">
                    Comments {actionType === 'reject' ? '(Recommended)' : '(Optional)'}
                  </label>
                  <textarea
                    id="comments"
                    name="comments"
                    value={actionComments}
                    onChange={(e) => setActionComments(e.target.value)}
                    placeholder={
                      actionType === 'approve' 
                        ? 'Enter any comments about the approval...'
                        : 'Enter reason for rejection...'
                    }
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={sendInEnglish}
                      onChange={(e) => setSendInEnglish(e.target.checked)}
                    />
                    <span>Send email in English</span>
                  </label>
                  <small className="form-hint">Default: Spanish</small>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => setShowActionModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`submit-button ${actionType === 'approve' ? 'approve-button' : 'reject-button-confirm'}`}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={actionType === 'approve' ? faCheck : faTimes} />
                        {actionType === 'approve' ? 'Approve Request' : 'Reject Request'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail View Modal */}
      {showDetailModal && selectedDetailRequest && (
        <div className="modal-overlay" onClick={handleCloseDetailModal}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faInfoCircle} />
                Verification Request Details
              </h3>
              <button
                className="close-button"
                onClick={handleCloseDetailModal}
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body detail-modal-body">
              {/* Status Badge */}
              <div className="detail-section">
                <div className="detail-section-header">
                  <h4>Status</h4>
                  <div 
                    className="status-badge large" 
                    style={{ backgroundColor: getStatusColor(selectedDetailRequest.status) }}
                  >
                    <FontAwesomeIcon icon={getStatusIcon(selectedDetailRequest.status)} />
                    {selectedDetailRequest.status}
                  </div>
                </div>
              </div>

              {/* User Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faUser} /> User Information</h4>
                <div className="detail-rows">
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedDetailRequest.userName || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">IBO Number:</span>
                    <span className="detail-value ibo-number">{selectedDetailRequest.userIboNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{selectedDetailRequest.userEmail || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{selectedDetailRequest.userPhone || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Secondary Phone:</span>
                    <span className="detail-value">
                      {selectedDetailRequest.userSecondaryPhone 
                        ? `${selectedDetailRequest.userSecondaryPhone} ${selectedDetailRequest.userSecondaryPhoneVerified ? '(Verified)' : '(Not Verified)'}` 
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Team:</span>
                    <span className="detail-value">{selectedDetailRequest.userTeamName || 'Not Assigned'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Upline Platinum IBO:</span>
                    <span className="detail-value">{selectedDetailRequest.userUplinePlatinumIbo || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">First Contract Year:</span>
                    <span className="detail-value">
                      {selectedDetailRequest.userIsFirstContractYear ? (
                        <span className="badge-yes">Yes</span>
                      ) : (
                        <span className="badge-no">No</span>
                      )}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">User Created At:</span>
                    <span className="detail-value">{formatDate(selectedDetailRequest.userCreatedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Request Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faTag} /> Request Information</h4>
                <div className="detail-rows">
                  <div className="detail-row">
                    <span className="detail-label">Request Type:</span>
                    <span className="detail-value">
                      <span className={`request-type-badge ${selectedDetailRequest.requestType?.toLowerCase()}`}>
                        {selectedDetailRequest.requestType || 'N/A'}
                      </span>
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Approver:</span>
                    <span className="detail-value">
                      {selectedDetailRequest.isAdminRequest ? (
                        <span className="approver-badge admin">
                          <FontAwesomeIcon icon={faUserShield} /> Admin
                        </span>
                      ) : (
                        <span className="approver-badge upline">
                          <FontAwesomeIcon icon={faUser} /> {selectedDetailRequest.approverName || 'Upline'}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Requested At:</span>
                    <span className="detail-value">{formatDate(selectedDetailRequest.requestedAt)}</span>
                  </div>
                  {selectedDetailRequest.respondedAt && (
                    <>
                      <div className="detail-row">
                        <span className="detail-label">Responded At:</span>
                        <span className="detail-value">{formatDate(selectedDetailRequest.respondedAt)}</span>
                      </div>
                      {selectedDetailRequest.processingTimeInMinutes !== null && (
                        <div className="detail-row">
                          <span className="detail-label">Processing Time:</span>
                          <span className="detail-value">{selectedDetailRequest.processingTimeInMinutes} minutes</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Comments */}
              {selectedDetailRequest.comments && (
                <div className="detail-section">
                  <h4><FontAwesomeIcon icon={faInfoCircle} /> Comments</h4>
                  <div className="comments-box">
                    {selectedDetailRequest.comments}
                  </div>
                </div>
              )}

              {/* Actions for Pending Requests */}
              {selectedDetailRequest.status === 'PENDING' && (
                <div className="detail-section detail-actions">
                  <button 
                    className="action-button refresh-kpi-button"
                    onClick={(e) => handleRefreshKpi(selectedDetailRequest.userIboNumber, e)}
                    disabled={refreshingKpi === selectedDetailRequest.userIboNumber}
                    title="Refresh KPI data from Amway"
                  >
                    <FontAwesomeIcon 
                      icon={faSync} 
                      spin={refreshingKpi === selectedDetailRequest.userIboNumber}
                    />
                    {refreshingKpi === selectedDetailRequest.userIboNumber ? ' Refreshing...' : ' Refresh KPI'}
                  </button>
                  <button 
                    className="action-button edit-button"
                    onClick={() => {
                      handleCloseDetailModal();
                      handleEditUser(selectedDetailRequest);
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    Edit User
                  </button>
                  <button 
                    className="action-button approve-button"
                    onClick={() => {
                      handleCloseDetailModal();
                      handleApproveRequest(selectedDetailRequest);
                    }}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    Approve
                  </button>
                  <button 
                    className="action-button reject-button"
                    onClick={() => {
                      handleCloseDetailModal();
                      handleRejectRequest(selectedDetailRequest);
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
      />
    </div>
  );
};

export default UserVerificationPage;

